import type { HotelOffersResponse, HotelResponse, OfferResponse, OffersResponse } from '@/interfaces';
import { type QueryParams, stringifyParams } from '@/lib/utils/queryString';

const apiFetch = async <T>(url: string): Promise<T | null> => {
    const response = await fetch(url);
    if (response.status === 304) {
        return null; // Not modified
    }
    if (!response.ok) {
        throw new Error(`(${response.status}) ${response.statusText}`);
    }
    return response.json();
};

export const fetchOffers = async (baseUrl: string, params: QueryParams = {}): Promise<OffersResponse | null> => {
    const query = stringifyParams(params);
    const url = `${baseUrl}/offers${query ? `?${query}` : ''}`;
    return apiFetch<OffersResponse>(url);
};

/**
 * Fetch multiple pages of offers for map view.
 * Caps at MAX_PAGES to avoid overwhelming the API with too many requests.
 * For 20k+ hotels, users should zoom in / use filters to narrow results.
 */
const MAX_PAGES = 10; // ~200-500 offers depending on page size
const BATCH_SIZE = 5; // Fetch 5 pages at a time to avoid connection limits

export const fetchAllOffers = async (baseUrl: string, params: QueryParams = {}): Promise<OffersResponse | null> => {
    // Fetch first page to get total count
    const firstPage = await fetchOffers(baseUrl, { ...params, page: '1' });
    if (!firstPage) return null;

    const totalPages = Math.min(firstPage.totalPages, MAX_PAGES);
    if (totalPages <= 1) {
        return firstPage;
    }

    // Fetch remaining pages in batches to avoid overwhelming the API
    const allOffers = [...firstPage.offers];

    for (let batchStart = 2; batchStart <= totalPages; batchStart += BATCH_SIZE) {
        const batchEnd = Math.min(batchStart + BATCH_SIZE - 1, totalPages);
        const batchPromises: Promise<OffersResponse | null>[] = [];

        for (let page = batchStart; page <= batchEnd; page++) {
            batchPromises.push(fetchOffers(baseUrl, { ...params, page: String(page) }));
        }

        const batchResults = await Promise.all(batchPromises);
        for (const page of batchResults) {
            if (page) {
                allOffers.push(...page.offers);
            }
        }
    }

    return {
        ...firstPage,
        offers: allOffers,
        currentPage: 1,
        totalPages: 1,
        // Keep original totalOffers so UI can show "showing X of Y"
    };
};

export const fetchOffer = async (baseUrl: string, params: QueryParams = {}): Promise<OfferResponse | null> => {
    const query = stringifyParams(params);
    const url = `${baseUrl}/offer${query ? `?${query}` : ''}`;
    return apiFetch<OfferResponse>(url);
};

export const fetchOffersForHotel = async (baseUrl: string, params: QueryParams = {}): Promise<HotelOffersResponse | null> => {
    const { hotelId, ...rest } = params;
    const query = stringifyParams(rest);
    const url = `${baseUrl}/hotel/offers/${encodeURIComponent(hotelId as string)}${query ? `?${query}` : ''}`;
    return apiFetch<HotelOffersResponse>(url);
};

export const fetchHotel = async (baseUrl: string, params: QueryParams = {}): Promise<HotelResponse | null> => {
    const { hotelId, preferredSupplier } = params;
    const url = `${baseUrl}/hotel/${encodeURIComponent(hotelId as string)}${preferredSupplier ? `?preferredSupplier=${encodeURIComponent(preferredSupplier as string)}` : ''}`;
    return apiFetch<HotelResponse>(url);
};
